import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaTrash, FaPlus, FaBuilding } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { typeIcons, locationLabel } from '../components/Utils';
import Table from '../components/Table';
import Modal from '../components/Modal';
import axios from "../axiosConfig";
import './Locations.css';
import PageTitle from "../components/PageTitle";

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, location: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/locations/', {
        withCredentials: true,
      });
      
      setLocations(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (location) => {
    setDeleteModal({ show: true, location });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.location) return;

    try {
      setDeleting(true);
      await axios.delete(`/locations/${deleteModal.location.id}/`, {
        withCredentials: true,
      });

      // Remove the deleted location from the state
      setLocations(locations.filter(loc => loc.id !== deleteModal.location.id));
      setDeleteModal({ show: false, location: null });
      setError(null);
    } catch (err) {
      console.error('Error deleting location:', err);
      setError('Failed to delete location');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ show: false, location: null });
  };

  const formatAddress = (location) => {
    const addressParts = [
      location.address_1,
      location.address_2,
      location.address_3,
      location.address_4,
      location.city,
      location.county,
      location.country,
      location.postcode
    ].filter(part => part && part.trim() !== '');
    
    return addressParts.join(', ');
  };

  const columns = [
    {
      key: 'type',
      header: 'Address Type',
      type: 'discrete',
      width: 80,
      render: (value) => value || '—'
    },
    {
      key: 'venue',
      header: 'Venue',
      type: 'discrete',
      width: 64,
      render: (value) => (value ? 'Yes' : 'No')
    },
    {
      key: 'account_name',
      header: 'Account',
      type: 'alphabetical',
      width: 112,
      render: (value, row) => (
        value ? (
          <Link to={`/account?id=${row.account_id}`}>
            {typeIcons.account} {value}
          </Link>
        ) : '—'
      )
    },
    {
      key: 'address',
      header: 'Address',
      type: 'static',
      width: 160,
      render: (value, row) => formatAddress(row) || '—'
    },
    {
      key: 'city',
      header: 'City',
      type: 'alphabetical',
      width: 80
    },
    {
      key: 'county',
      header: 'County',
      type: 'alphabetical',
      width: 80
    },
    {
      key: 'country',
      header: 'Country',
      type: 'discrete',
      width: 80
    },
    {
      key: 'postcode',
      header: 'Postcode',
      type: 'static',
      width: 64
    },
    {
      key: 'switchboard_number',
      header: 'Switchboard',
      type: 'static',
      width: 96,
      render: (value, row) => {
        if (!value) return '—';
        // You might want to add country code logic here if available
        return value;
      }
    },
    {
      key: 'actions',
      header: 'Actions',
      type: 'static',
      sortable: false,
      width: 64,
      render: (value, row) => (
        <button
          className="btn-danger-outline btn-small"
          onClick={() => handleDeleteClick(row)}
          title="Delete location"
        >
          <FaTrash />
        </button>
      )
    }
  ];

  if (loading) {
    return (
      <div className="page-layout">
        <div className="page-content">
          <PageTitle>{typeIcons.location} Locations</PageTitle>
          <div className="loading">Loading locations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-layout">
      <PageTitle>{typeIcons.location} Locations</PageTitle>
      <div className="page-header">
        <div className="page-actions">
          <button className="btn-primary">
            <FaPlus /> Add Location
          </button>
        </div>
      </div>
      
      <div className="page-content">

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <Table
          data={locations}
          columns={columns}
          loading={loading}
          emptyMessage="No locations found."
          defaultSortColumn="account_name"
          defaultSortDirection="asc"
        />

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={deleteModal.show}
          onClose={handleDeleteCancel}
          title="Delete Location"
        >
          <p>
            Are you sure you want to delete the location "{locationLabel(deleteModal.location)}"?
          </p>
          {formatAddress(deleteModal.location) && (
            <p className="location-address-preview">
              <em>{formatAddress(deleteModal.location)}</em>
            </p>
          )}
          <p className="warning-text">
            This action cannot be undone.
          </p>
          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={handleDeleteCancel}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              className="btn-danger"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Location'}
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Locations;
